import { useCallback, useEffect, useRef, useState } from 'react';

import {
  IMAGE_BLOCK_ASPECT_VALUES,
  type ImageBlockAspect,
} from '@/components/blocks/primitives/image-presentation';
import type { CollectionCardAlign, CollectionCardField } from '@/lib/blocks/collection/blocks';
import { CARD_ALIGN_VALUES, resolveCardConfigFromTemplate } from '@/lib/blocks/collection/blocks';
import type { LayoutVariantEntry } from '@/lib/blocks/collection/variant-registry';
import { getMainSections } from '@/lib/page-utils';
import { pickStringUnion, readRecord, readString } from '@/lib/prop-utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';

export interface VariantPropsPersistence {
  storageKey?: string;
  scopeKey?: string;
  sharedKeys?: readonly string[];
  sharedScopeKeys?: readonly string[];
}

export interface UseCollectionSettingsOptions {
  sectionId: string;
  block: PageBlock;
  cardTemplateBlock?: PageBlock;
  cardTemplateChildren?: PageBlock[];
  variants: LayoutVariantEntry[];
  variantProps?: VariantPropsPersistence;
  getPersistedProps?: (props: Record<string, unknown>) => Record<string, unknown>;
  onOpenChange?: (open: boolean) => void;
}

const EMPTY_CARD_TEMPLATE_CHILDREN: PageBlock[] = [];
const EMPTY_SHARED_KEYS: string[] = [];

function readLatestBlockProps(sectionId: string, blockId: string) {
  return getMainSections(usePageEditorStore.getState().page)
    .find((section) => section.id === sectionId)
    ?.blocks?.find((candidate) => candidate.id === blockId)?.props;
}

export function useCollectionSettings({
  sectionId,
  block,
  cardTemplateBlock,
  cardTemplateChildren = EMPTY_CARD_TEMPLATE_CHILDREN,
  variants,
  variantProps,
  getPersistedProps,
  onOpenChange,
}: UseCollectionSettingsOptions) {
  const {
    storageKey: variantPropsStorageKey = 'variantProps',
    scopeKey: variantPropsScopeKey,
    sharedKeys: variantPropsSharedKeys = EMPTY_SHARED_KEYS,
    sharedScopeKeys: variantPropsSharedScopeKeys = EMPTY_SHARED_KEYS,
  } = variantProps ?? {};
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const [open, setOpen] = useState(false);
  const [draftVariant, setDraftVariant] = useState('');
  const [draftVariantProps, setDraftVariantProps] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const [draftFields, setDraftFields] = useState<CollectionCardField[]>([]);
  const wasOpenRef = useRef(false);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    const blockProps = block.props ?? {};
    const rawVariant = readString(blockProps.variant);
    const variant =
      rawVariant && variants.some((entry) => entry.key === rawVariant)
        ? rawVariant
        : (variants[0]?.key ?? '');
    const nextVariantProps =
      (readRecord(blockProps[variantPropsStorageKey]) as Record<
        string,
        Record<string, unknown>
      > | null) ?? {};

    setDraftVariant(variant);
    setDraftVariantProps(nextVariantProps);

    if (cardTemplateBlock) {
      setDraftFields(resolveCardConfigFromTemplate(cardTemplateBlock, cardTemplateChildren).fields);
    } else {
      setDraftFields([]);
    }
  }, [open, block, cardTemplateBlock, cardTemplateChildren, variants, variantPropsStorageKey]);

  const activeVariantEntry = variants.find((v) => v.key === draftVariant) ?? variants[0];
  const activeVariantProps = (draftVariantProps[variantPropsScopeKey || draftVariant] ??
    draftVariantProps[draftVariant] ??
    activeVariantEntry?.defaultProps ??
    {}) as Record<string, unknown>;

  const rawAlign = readString(activeVariantProps.align);
  const draftCardAlign: CollectionCardAlign = (CARD_ALIGN_VALUES as readonly string[]).includes(
    rawAlign ?? '',
  )
    ? (rawAlign as CollectionCardAlign)
    : 'start';

  const patchBlockProps = useCallback(
    (patch: Record<string, unknown>) => {
      const latestProps = readLatestBlockProps(sectionId, block.id) ?? block.props;
      const baseProps = getPersistedProps?.(latestProps ?? {}) ?? latestProps ?? {};
      updateBlock(sectionId, block.id, { props: { ...baseProps, ...patch } });
    },
    [block.id, block.props, getPersistedProps, sectionId, updateBlock],
  );

  const resolveNextVariantProps = useCallback(
    (
      patch: Record<string, unknown>,
      currentDraftVariantProps: Record<string, Record<string, unknown>>,
    ) => {
      const variantPropsKey = variantPropsScopeKey || draftVariant;
      const sharedPatch = Object.fromEntries(
        Object.entries(patch).filter(([key]) => variantPropsSharedKeys.includes(key)),
      );
      const hasSharedPatch = Object.keys(sharedPatch).length > 0;
      const nextSpecific = {
        ...(currentDraftVariantProps[variantPropsKey] ??
          currentDraftVariantProps[draftVariant] ??
          activeVariantEntry?.defaultProps ??
          {}),
        ...patch,
      };
      let nextVariantProps = { ...currentDraftVariantProps, [variantPropsKey]: nextSpecific };

      if (hasSharedPatch) {
        // Keep card-level visual props in sync while legacy data stores them per layout variant.
        const scopeKeys = new Set([
          ...variantPropsSharedScopeKeys,
          ...Object.keys(currentDraftVariantProps),
          variantPropsKey,
        ]);

        scopeKeys.forEach((scopeKey) => {
          if (scopeKey === variantPropsKey) return;
          nextVariantProps = {
            ...nextVariantProps,
            [scopeKey]: {
              ...(nextVariantProps[scopeKey] ?? {}),
              ...sharedPatch,
            },
          };
        });
      }

      return nextVariantProps;
    },
    [
      activeVariantEntry?.defaultProps,
      draftVariant,
      variantPropsScopeKey,
      variantPropsSharedKeys,
      variantPropsSharedScopeKeys,
    ],
  );

  const handleVariantChange = useCallback(
    (key: string) => {
      let nextVariantProps = draftVariantProps;
      if (!nextVariantProps[key]) {
        const entry = variants.find((v) => v.key === key);
        if (entry) {
          nextVariantProps = { ...nextVariantProps, [key]: entry.defaultProps };
        }
      }
      setDraftVariant(key);
      setDraftVariantProps(nextVariantProps);
      patchBlockProps({ variant: key, [variantPropsStorageKey]: nextVariantProps });
    },
    [draftVariantProps, patchBlockProps, variantPropsStorageKey, variants],
  );

  const handleVariantPropsChange = useCallback(
    (patch: Record<string, unknown>) => {
      setDraftVariantProps((currentDraftVariantProps) => {
        const nextVariantProps = resolveNextVariantProps(patch, currentDraftVariantProps);
        patchBlockProps({ variant: draftVariant, [variantPropsStorageKey]: nextVariantProps });
        return nextVariantProps;
      });
    },
    [draftVariant, patchBlockProps, resolveNextVariantProps, variantPropsStorageKey],
  );

  const handleCardAlignChange = useCallback(
    (align: CollectionCardAlign) => handleVariantPropsChange({ align }),
    [handleVariantPropsChange],
  );

  const imageField = draftFields.find((field) => field.kind === 'image' && field.blockId);
  const imageAspect = imageField
    ? pickStringUnion(readString(imageField.blockProps?.aspect), IMAGE_BLOCK_ASPECT_VALUES, 'wide')
    : undefined;
  const canSetImageAspect = !!imageField && readString(imageField.blockProps?.shape) !== 'circle';

  const handleImageAspectChange = useCallback(
    (aspect: ImageBlockAspect) => {
      if (!imageField?.blockId) return;
      const latestProps =
        readLatestBlockProps(sectionId, imageField.blockId) ?? imageField.blockProps;
      const blockProps = { ...(latestProps ?? {}), aspect };
      updateBlock(sectionId, imageField.blockId, { props: blockProps });
      setDraftFields((fields) =>
        fields.map((field) =>
          field.blockId === imageField.blockId ? { ...field, blockProps } : field,
        ),
      );
    },
    [imageField, sectionId, updateBlock],
  );

  return {
    open,
    handleOpenChange,
    draftVariant,
    draftFields,
    setDraftFields,
    activeVariantEntry,
    activeVariantProps,
    draftCardAlign,
    handleVariantChange,
    handleVariantPropsChange,
    handleCardAlignChange,
    patchBlockProps,
    imageAspect,
    handleImageAspectChange: canSetImageAspect ? handleImageAspectChange : undefined,
  };
}
